import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { MiscModel } from "@models/misc-model";
import { TranslatePipe } from '@ngx-translate/core';
import { SubMenuStateEnum, SubMenuStateTogglerService } from "@services/sub-menu-state-toggler.service";
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { ConfigurationModel } from '@models/configuration';
import { CustomizerMenuPartModel } from '@models/customizer-menu-part';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { EventsService } from '@services/events.service';
import { BaseCustomizerComponent } from '@shared/base-customizer-component';
import { SubmenuTransition } from "../../../../shared/transitions/submenu.transition";
import { EnduserDisplayService } from './../../../../_services/enduserdisplay.service';

@Component({
	selector: "customizer-menu",
	templateUrl: "customizer-menu.html",
	styleUrls: ['./_customizer-menu.scss'],
	animations: [
		SubmenuTransition()
	],
	imports: [CommonModule, TranslatePipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true
})
export class CustomizerMenuComponent implements OnChanges {

	@Input() blocked: boolean;
	@Input() isReady: boolean;
	@Input() components: Array<CustomizerMenuPartModel>;
	@Input() currentSubComponent: CustomizerMenuPartModel | undefined;
	@Input() currentMainComponent?: CustomizerMenuPartModel;
	@Input() configurations: Array<ConfigurationModel>;
	@Input() chosenVariants?: Array<ComponentVariantModel>;
	@Input() modelSize?: ModelSizeModel;
	@Input() model?: ModelModel;
	@Input() modelGroup?: ModelGroupModel;
	@Input() chosenCase?: MiscModel | null;

	@Output() currentComponentChange = new EventEmitter<CustomizerMenuPartModel | undefined>();
	@Output() currentMainComponentChange = new EventEmitter<CustomizerMenuPartModel | undefined>();

	constructor(
		private eventService: EventsService,
		private enduserDisplayService: EnduserDisplayService,
		private subMenuStateTogglerService: SubMenuStateTogglerService
	) {
		this.subMenuStateTogglerService.toggle.subscribe(s => {
			switch (s) {
				case SubMenuStateEnum.configOpen:
					this.setCurrentMainComponent(null);
					break;

				default:
					break;
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.components.forEach(component => {
			const subComponentsToShowList = new Array<CustomizerMenuPartModel>();

			component.subComponents.forEach(sub => {
				if (this.shouldShowComponent(sub)) {
					subComponentsToShowList.push(sub);
				}
			});

			component.subComponentsToShow = subComponentsToShowList;
		});
	}

	public getState(component: CustomizerMenuPartModel): string {
		if (component.parentMenu == this.currentMainComponent) {
			if (component.parentMenu && component.parentMenu.component) {
				if (component.parentMenu.collapsed) {
					return "collapse";
				}
			}

			return "expand";
		}
		else {
			return "collapse";
		}
	}

	public setCurrentMainComponent(component: CustomizerMenuPartModel | null) {
		if (!component) {
			this.currentMainComponentChange.emit(undefined);
			this.currentComponentChange.emit(undefined);
		}
		else if (this.currentMainComponent && this.currentMainComponent.id == component.id && !this.currentSubComponent) {
			this.currentMainComponentChange.emit(undefined);
			this.currentComponentChange.emit(undefined);

			this.subMenuStateTogglerService.toggle.emit(SubMenuStateEnum.menuClose);
		}
		else if (!component.component) {
			this.currentMainComponentChange.emit(component);
			this.currentComponentChange.emit(undefined);

			this.subMenuStateTogglerService.toggle.emit(SubMenuStateEnum.menuOpen);
		}
		else {
			this.eventService.showLoader(false);
			component.isVisible = true;
			this.currentMainComponentChange.emit(component);
			this.currentComponentChange.emit(undefined);

			this.subMenuStateTogglerService.toggle.emit(SubMenuStateEnum.menuOpen);
		}
	}

	public setCurrentSubComponent(component: CustomizerMenuPartModel) {
		if (this.currentSubComponent && component.id == this.currentSubComponent.id) {
			this.currentComponentChange.emit(undefined);
		}
		else {
			this.currentComponentChange.emit(component);
			this.eventService.showLoader(false);
			this.subMenuStateTogglerService.toggle.emit(SubMenuStateEnum.menuOpen);
		}
	}

	public IsSelected(componentId: number): boolean {
		return (this.currentMainComponent && componentId == this.currentMainComponent.id) ?? false;
	}

	public ComponentIsPlaceholderForSubcomponents(component: CustomizerMenuPartModel): boolean {
		if (component.collapsed) {
			return false;
		}
		else {
			return true;
		}
	}

	public shouldComponentShowVisited(component: CustomizerMenuPartModel) {
		if (component.subComponents && component.subComponents.length > 0) {
			return component.subComponents.filter(sub => !sub.isVisited).length == 0;
		}
		else {
			return component.isVisited;
		}
	}

	public shouldShowComponent(component: CustomizerMenuPartModel) {
		if (component.component) {
			return (component.component as typeof BaseCustomizerComponent).shouldBeVisible(this.modelGroup, component, this.chosenVariants, this.configurations, this.enduserDisplayService.IsDisplay(), this.chosenCase);
		}
		else {
			let subcomponentContainsVariants = false;

			component.subComponents.forEach(subComponent => {
				if ((subComponent.component as typeof BaseCustomizerComponent)?.shouldBeVisible(this.modelGroup, component, this.chosenVariants, this.configurations, this.enduserDisplayService.IsDisplay(), this.chosenCase)) {
					subcomponentContainsVariants = true;
				}
			});

			return subcomponentContainsVariants;
		}
	}

}
